import { pickContenidoForPestanas } from "../../../../utils/normalizeCourseShape";
import { parseCourseTagsInput } from "../../../../utils/parseCourseTagsInput";
import { buildCourseTestsPayloadObject } from "./editarCursoServerFormat";

/**
 * @param {{ pestanasSerialized?: string } | null | undefined} baseline
 * @returns {string[]}
 */
export function previousTabsFromCourseBaseline(baseline) {
  if (!baseline || typeof baseline.pestanasSerialized !== "string") {
    return [];
  }
  try {
    const parsed = JSON.parse(baseline.pestanasSerialized);
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Snapshot of course fields used to detect which keys the editor user actually changed.
 * @param {object} input
 * @param {object} input.data - raw GET /api/admin/cursos/:id payload
 * @param {unknown[]} input.testsArr
 * @param {string[]} input.pestanas
 * @param {object} input.contenidoNormalized
 * @param {unknown[]} input.adminIdsFromLoad
 * @param {string} input.rol
 * @param {(id: unknown) => string} input.adminIdKeyFn
 * @returns {object}
 */
export function buildCourseEditBaselineFromLoad(input) {
  const {
    data,
    testsArr,
    pestanas,
    contenidoNormalized,
    adminIdsFromLoad,
    adminIdKeyFn,
  } = input;
  const fcRaw = data.fechaCierre ? new Date(data.fechaCierre) : null;
  const fechaCierre =
    fcRaw && !Number.isNaN(fcRaw.getTime()) ? fcRaw.toISOString().slice(0, 10) : "";
  const testsPayload = buildCourseTestsPayloadObject(Array.isArray(testsArr) ? testsArr : []);
  const contenidoPayload = pickContenidoForPestanas(contenidoNormalized, pestanas);
  const adminKeys = (Array.isArray(adminIdsFromLoad) ? adminIdsFromLoad : [])
    .map((x) => adminIdKeyFn(x))
    .filter((k) => typeof k === "string" && k.trim() !== "")
    .sort();
  return {
    titulo: data.titulo != null ? String(data.titulo) : "",
    descripcion: data.descripcion != null ? String(data.descripcion) : "",
    duracion: data.duracion != null ? String(data.duracion) : "",
    imagen: data.imagen != null ? String(data.imagen) : "",
    precio: data.precio === null || data.precio === undefined || data.precio === "" ? "" : String(data.precio),
    descuento:
      data.descuento === null || data.descuento === undefined || data.descuento === "" ? "" : String(data.descuento),
    codigoDescuento: data.codigoDescuento != null ? String(data.codigoDescuento) : "",
    precioFinal:
      data.precioFinal === null || data.precioFinal === undefined || data.precioFinal === ""
        ? ""
        : String(data.precioFinal),
    esAbierto: data.esAbierto === true,
    fechaCierre,
    contenidoDescargable: data.contenidoDescargable === true,
    pestanasSerialized: JSON.stringify(Array.isArray(pestanas) ? pestanas : []),
    testsSerialized: JSON.stringify(testsPayload),
    contenidoSerialized: JSON.stringify(contenidoPayload),
    adminIdsSerialized: adminKeys.join("|"),
  };
}

/**
 * @param {object} input - current editor state (same shape as PUT body builders)
 * @returns {object}
 */
export function buildCourseEditSnapshotForSave(input) {
  const {
    titulo,
    descripcion,
    duracion,
    imagen,
    precio,
    descuento,
    codigoDescuento,
    precioFinal,
    esAbierto,
    fechaCierre,
    contenidoDescargable,
    pestanasCurso,
    tests,
    bloquesContenido,
    adminIds,
    tagsInput,
    disponibleCompra,
    adminIdKeyFn,
  } = input;
  const testsPayload = buildCourseTestsPayloadObject(Array.isArray(tests) ? tests : []);
  const contenidoPayload = pickContenidoForPestanas(bloquesContenido, pestanasCurso);
  const adminKeys = (Array.isArray(adminIds) ? adminIds : [])
    .map((x) => adminIdKeyFn(x))
    .filter((k) => typeof k === "string" && k.trim() !== "")
    .sort();
  return {
    titulo: titulo != null ? String(titulo) : "",
    descripcion: descripcion != null ? String(descripcion) : "",
    duracion: duracion != null ? String(duracion) : "",
    imagen: imagen != null ? String(imagen) : "",
    precio: precio === "" || precio === null || precio === undefined ? "" : String(precio),
    descuento: descuento === "" || descuento === null || descuento === undefined ? "" : String(descuento),
    codigoDescuento: codigoDescuento != null ? String(codigoDescuento) : "",
    precioFinal: precioFinal === "" || precioFinal === null || precioFinal === undefined ? "" : String(precioFinal),
    esAbierto: esAbierto === true,
    fechaCierre: fechaCierre != null ? String(fechaCierre).trim() : "",
    contenidoDescargable: contenidoDescargable === true,
    pestanasSerialized: JSON.stringify(Array.isArray(pestanasCurso) ? pestanasCurso : []),
    testsSerialized: JSON.stringify(testsPayload),
    contenidoSerialized: JSON.stringify(contenidoPayload),
    adminIdsSerialized: adminKeys.join("|"),
    tagsSerialized: JSON.stringify(parseCourseTagsInput(typeof tagsInput === "string" ? tagsInput : "")),
    disponibleCompra: disponibleCompra !== false,
  };
}

/**
 * @param {object | null} baseline
 * @param {object} snapshot
 * @returns {string[] | null} null = no baseline yet (omit camposTocados); [] = no changes
 */
export function listCamposTocadosChanged(baseline, snapshot) {
  if (!baseline) {
    return null;
  }
  /** @type {string[]} */
  const touched = [];
  if (snapshot.titulo !== baseline.titulo) {
    touched.push("titulo");
  }
  if (snapshot.descripcion !== baseline.descripcion) {
    touched.push("descripcion");
  }
  if (snapshot.duracion !== baseline.duracion) {
    touched.push("duracion");
  }
  if (snapshot.imagen !== baseline.imagen) {
    touched.push("imagen");
  }
  if (snapshot.precio !== baseline.precio) {
    touched.push("precio");
  }
  if (snapshot.descuento !== baseline.descuento) {
    touched.push("descuento");
  }
  if (snapshot.codigoDescuento !== baseline.codigoDescuento) {
    touched.push("codigoDescuento");
  }
  if (snapshot.precioFinal !== baseline.precioFinal) {
    touched.push("precioFinal");
  }
  if (snapshot.esAbierto !== baseline.esAbierto) {
    touched.push("esAbierto");
  }
  if (snapshot.fechaCierre !== baseline.fechaCierre) {
    touched.push("fechaCierre");
  }
  if (snapshot.contenidoDescargable !== baseline.contenidoDescargable) {
    touched.push("contenidoDescargable");
  }
  if (snapshot.pestanasSerialized !== baseline.pestanasSerialized) {
    touched.push("pestanasCurso");
  }
  if (snapshot.testsSerialized !== baseline.testsSerialized) {
    touched.push("tests");
  }
  if (snapshot.contenidoSerialized !== baseline.contenidoSerialized) {
    touched.push("contenido");
  }
  if (snapshot.adminIdsSerialized !== baseline.adminIdsSerialized) {
    touched.push("administradores");
  }
  if (snapshot.tagsSerialized !== baseline.tagsSerialized) {
    touched.push("tags");
  }
  if (snapshot.disponibleCompra !== baseline.disponibleCompra) {
    touched.push("disponibleCompra");
  }
  return touched;
}

/**
 * @param {object | null} baseline
 * @param {object} snapshot
 * @returns {boolean}
 */
export function hasUnsavedCourseEdits(baseline, snapshot) {
  if (!baseline) {
    return false;
  }
  const normalizedBaseline = {
    ...baseline,
    tagsSerialized: baseline.tagsSerialized ?? snapshot.tagsSerialized,
    disponibleCompra: baseline.disponibleCompra ?? snapshot.disponibleCompra,
  };
  const touched = listCamposTocadosChanged(normalizedBaseline, snapshot);
  return Array.isArray(touched) && touched.length > 0;
}
